import { useTranslation } from "react-i18next";
import { i18n } from "@tellescope/i18n";
import type { ChartDataPoint } from "./useVitalsChart";

const getUnitToDisplayUnitMap = (): Record<
  string,
  {
    lead?: string;
    trailing?: string;
  }
> => ({
  SBP: { lead: i18n.t("SBP", { ns: "vitals" }), trailing: i18n.t("mmHg", { ns: "vitals" }) },
  DBP: { lead: i18n.t("DBP", { ns: "vitals" }), trailing: i18n.t("mmHg", { ns: "vitals" }) },
  BPM: { lead: i18n.t("Heart Rate (BPM)", { ns: "vitals" }) },
  LB: { trailing: i18n.t("lbs", { ns: "vitals" }) },
  "mg/dL": { lead: i18n.t("Glucose (mg/dL)", { ns: "vitals" }) },
  Height: { trailing: i18n.t("in", { ns: "vitals" }) },
});

const isChartDataPoint = (value: unknown): value is ChartDataPoint =>
  value !== null && typeof value === "object" && "timestamp" in value && "value" in value;

export interface VitalsChartTooltipProps {
  /** The unit the chart is currently plotting, used to label the value. */
  activeUnit: string;
  /** Supplied by recharts when the tooltip is shown. */
  active?: boolean;
  /** Supplied by recharts; the hovered point is carried on the first entry's `payload`. */
  payload?: { payload?: unknown }[];
}

/**
 * The hover tooltip for {@link VitalsChart}.
 *
 * Passed to recharts' `Tooltip` as an element rather than a render callback, so the JSX lives in a
 * component of its own. Recharts' payload is untyped, hence the guard rather than a cast.
 */
export const VitalsChartTooltip = ({ activeUnit, active, payload }: VitalsChartTooltipProps) => {
  const { t } = useTranslation("vitals");

  if (!active || !payload?.length) return null;

  const point = payload[0]?.payload;
  if (!isChartDataPoint(point)) return null;

  const date = new Date(point.timestamp);
  const dateStr = date.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  const timeStr = date.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  const { lead = "", trailing = "" } = getUnitToDisplayUnitMap()[activeUnit] ?? {
    lead: "",
    trailing: activeUnit,
  };

  return (
    <div className="rounded-lg border border-border bg-background px-2.5 py-1.5 text-xs shadow-md space-y-1">
      <div className="text-muted-foreground">
        {dateStr} {timeStr}
      </div>
      <div className="font-medium" style={{ color: point.color }}>
        {lead
          ? t("{{lead}}: {{value}} {{trailing}}", { lead, trailing, value: point.value }).trim()
          : t("{{value}} {{trailing}}", { value: point.value, trailing })}
      </div>
    </div>
  );
};
